/**
 * buildPhotoCloudRecord — converts a local photo (Blob + metadata) into the
 * cloud record shape defined in DESIGN-BRIEF §3.3.
 *
 * FT-344 (2026-07-08) — sync collection `sr.photos.v1` payload builder.
 *
 * Base64 encoding notes:
 *  - Full-res photo → `data:image/jpeg;base64,...` data-URL string.
 *  - Thumbnail → `thumbData` data-URL string.
 *  - Base64 grows byte size ~33%; a 400 KB JPEG lands ~530 KB after encode.
 *    See DESIGN-BRIEF §3.1 trip-wire (500 photos/vessel).
 *  - Uses `FileReader.readAsDataURL` (available in browsers + jsdom test env).
 *
 * Timestamps:
 *  - `updatedAt: Date.now()` — first push.
 *  - `syncedAt: 0` — the sync bridge stamps this on successful cloud write.
 *    Matches the pattern used by `outbox.processOutbox` (which overwrites
 *    `syncedAt` with `Date.now()` on push, and mirrors the merge semantics
 *    the sync bridge uses to decide winner on conflict — LAST_WRITE_WINS via
 *    server timestamp per moduleMap §SYNC_POLICY).
 */

import { assemblePhotoCloudRecord } from '../../../../utils/photoPayloadBudget';

/**
 * @typedef {Object} PhotoRow
 * @property {string} photoId
 * @property {string} vesselId
 * @property {string} recordType
 * @property {string} recordId
 * @property {Blob}   blob
 * @property {string} mime
 * @property {number} [width]
 * @property {number} [height]
 * @property {number} [bytes]
 * @property {number} capturedAt
 * @property {string} capturedBy
 * @property {string} [caption]
 */

/**
 * Convert a Blob → base64 data-URL string via FileReader.
 * Returns e.g. `"data:image/jpeg;base64,/9j/4AAQ..."`.
 *
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
export const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  if (!(blob instanceof Blob)) {
    reject(new Error('[buildPhotoCloudRecord] blobToDataUrl: input must be a Blob'));
    return;
  }
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result));
  reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
  reader.readAsDataURL(blob);
});

/**
 * Build the cloud record for a single photo.
 *
 * Contract (from DESIGN-BRIEF §3.3):
 *   {
 *     id, vesselId, recordType, recordId,
 *     data (data-URL of full photo),
 *     thumbData (data-URL of thumbnail),
 *     mime, width, height, bytes,
 *     capturedAt, capturedBy, caption?,
 *     updatedAt, syncedAt
 *   }
 *
 * @param {{ photoData: PhotoRow, thumbBlob: Blob, vesselId: string }} args
 * @returns {Promise<Object>}
 */
export const buildPhotoCloudRecord = async ({ photoData, thumbBlob, vesselId }) => {
  if (!photoData?.photoId) {
    throw new Error('[buildPhotoCloudRecord] photoData.photoId is required');
  }
  if (!(photoData.blob instanceof Blob)) {
    throw new Error('[buildPhotoCloudRecord] photoData.blob must be a Blob');
  }
  if (!(thumbBlob instanceof Blob)) {
    throw new Error('[buildPhotoCloudRecord] thumbBlob must be a Blob');
  }
  if (!vesselId) {
    throw new Error('[buildPhotoCloudRecord] vesselId is required');
  }

  // Two base64 encodes run in parallel — small win for larger photos.
  const [data, thumbData] = await Promise.all([
    blobToDataUrl(photoData.blob),
    blobToDataUrl(thumbBlob),
  ]);

  // FT-577: field layout lives in ONE place (utils/photoPayloadBudget) so the
  // pre-send body-size measurement is guaranteed to match what is really sent.
  return assemblePhotoCloudRecord(
    photoData,
    { data, thumbData, fullBlobType: photoData.blob.type, fullBlobSize: photoData.blob.size },
    vesselId,
  );
};

export default buildPhotoCloudRecord;
